import type { HandRecord } from '@tctp/protocol';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { updateClock } from '../clock';
import { replaySteps } from '../replay';
import type { Verification } from '../useVerification';
import { TableView } from './TableView';

const STEP_MS = 1_400;
const NO_VERIFICATION: Verification = { status: 'idle', checks: [] };
const noop = async (): Promise<void> => {};

/**
 * A finished hand played back on the felt: the real table drawn from the record, one moment at a time, with a scrubber.
 * `history` and `onSelect` let the title's arrows move to the previous or next hand.
 */
export function ReplayView({ record, history = [], onSelect, onClose }: { record: HandRecord; history?: HandRecord[]; onSelect?: (r: HandRecord) => void; onClose: () => void }) {
  const steps = useMemo(() => replaySteps(record), [record]);
  const [i, setI] = useState(0);
  const at = history.findIndex(r => r.handId === record.handId);
  const older = at > 0 ? history[at - 1] : undefined;
  const newer = at >= 0 && at < history.length - 1 ? history[at + 1] : undefined;
  const select = (r: HandRecord | undefined): void => { if (r && onSelect) { setPlaying(false); setI(0); onSelect(r); } };
  const [playing, setPlaying] = useState(false);
  const clock = useMemo(() => updateClock({ offset: 0 }, Date.now()), []);
  useEffect(() => {
    if (!playing) return;
    if (i >= steps.length - 1) { setPlaying(false); return; }
    const t = setTimeout(() => setI(i + 1), STEP_MS);
    return () => clearTimeout(t);
  }, [playing, i, steps.length]);
  useEffect(() => {
    // Capture phase, and nothing gets past: the session modal underneath must not close on Escape, and the table's own shortcuts must not fire.
    const onKey = (e: KeyboardEvent): void => {
      e.stopImmediatePropagation();
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') { setPlaying(false); setI(n => Math.min(steps.length - 1, n + 1)); }
      else if (e.key === 'ArrowLeft') { setPlaying(false); setI(n => Math.max(0, n - 1)); }
      else if (e.key === ' ') { e.preventDefault(); setPlaying(p => !p); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose, steps.length]);
  const step = steps[i]!;
  return createPortal(
    <div className="replay-shell" role="dialog" aria-label={`Replay of hand #${record.handNumber}`}>
      <TableView snapshot={step.snapshot} history={[]} verification={NO_VERIFICATION} clock={clock} act={noop} roomId={step.snapshot.roomId} monitor />
      <div className="replay-top">
        <span className="replay-title">REPLAY · HAND #{record.handNumber}</span>
        <button type="button" className="replay-close" aria-label="Close replay" onClick={onClose}>✕</button>
      </div>
      <div className="replay-bar">
        <div className="replay-controls">
          <button type="button" className="hand" aria-label="Previous hand" disabled={!older} onClick={() => select(older)}>«</button>
          <button type="button" aria-label="Previous step" onClick={() => { setPlaying(false); setI(n => Math.max(0, n - 1)); }}>‹</button>
          <button type="button" className="play" aria-label={playing ? 'Pause' : 'Play'} onClick={() => setPlaying(p => !p)}>{playing ? '❚❚' : '▶'}</button>
          <button type="button" aria-label="Next step" onClick={() => { setPlaying(false); setI(n => Math.min(steps.length - 1, n + 1)); }}>›</button>
          <button type="button" className="hand" aria-label="Next hand" disabled={!newer} onClick={() => select(newer)}>»</button>
        </div>
        <input type="range" min={0} max={steps.length - 1} value={i} aria-label="Step" onChange={e => { setPlaying(false); setI(Number(e.target.value)); }} />
        <div className="replay-caption"><b>{step.label}</b><span>{step.caption}</span><span className="muted">{i + 1} / {steps.length}</span></div>
      </div>
    </div>,
    document.body,
  );
}
